import { useContext, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GlobalContext from '../context/GlobalContext.js';
import navTabs from '../config/navConfig.js';
import { Settings, LogOut, CircleUserRound, Sun, EyeOff, AArrowUp } from 'lucide-react';
import postAPI from '../functions/API/postAPI.js';

export default function DesktopNavBar({ unreadCount, agreementId, leftSlot }) {
    const { settings, theme, updateTheme, themeCSS, fontSize, updateFontSize } = useContext(GlobalContext);
    const location = useLocation();
    const navigate = useNavigate();
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const visibleTabs = navTabs.filter(tab => {
        if (!tab.settingsKey) return true;
        return settings && settings[tab.settingsKey] === 1;
    });

    const activeTab = (() => {
        const path = location.pathname;
        if (path === '/' || path === '/home') return 'home';
        if (path.startsWith('/repairs')) return 'repairs';
        if (path.startsWith('/appointments')) return 'home';
        if (path.startsWith('/requests')) return 'tasks';
        if (path.startsWith('/documents')) return 'documents';
        if (path.startsWith('/messages')) return 'messages';
        if (path.startsWith('/profile')) return null;
        return 'home';
    })();

    function navigateTo(tab) {
        let path = tab.path;
        if (tab.key === 'documents' && agreementId) {
            path = `/documents/${agreementId}`;
        } else if (tab.key === 'documents') {
            return;
        }
        if (path) {
            navigate(path);
        }
    }

    async function logout() {
        const body = { "": "" };
        let destroy = await postAPI('token/destroy', body);
        if (destroy.response.ok) {
            localStorage.clear();
            window.location.href = '/';
        }
    }

    return (
        <div className="hidden lg:flex w-full items-center gap-3">
            {/* Left: agreement switcher */}
            {leftSlot && <div className="shrink-0">{leftSlot}</div>}
            <div className="flex-1" />

            {/* Single nav pill: tabs + profile/settings right-aligned */}
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur-md rounded-full px-2 py-1.5">
                {/* Page tabs */}
                {visibleTabs.map((tab) => {
                    const isActive = activeTab === tab.key;
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => navigateTo(tab)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                                isActive
                                    ? 'bg-white font-semibold shadow-sm'
                                    : 'text-white/90 hover:bg-white/15'
                            }`}
                            style={isActive ? { color: primaryColor } : undefined}
                        >
                            <div className="relative">
                                <Icon size={18} strokeWidth={isActive ? 2.5 : 1.5} />
                                {tab.key === 'messages' && unreadCount > 0 && (
                                    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1 bg-red-500">
                                        {unreadCount}
                                    </span>
                                )}
                            </div>
                            {tab.label}
                        </button>
                    );
                })}

                {/* Divider */}
                <div className="w-px h-6 bg-white/30 mx-1" />

                {/* Profile */}
                <button
                    onClick={() => navigate('/profile')}
                    className={`flex items-center p-2 rounded-full text-sm transition-all ${
                        location.pathname === '/profile'
                            ? 'bg-white shadow-sm'
                            : 'text-white/90 hover:bg-white/15'
                    }`}
                    style={location.pathname === '/profile' ? { color: primaryColor } : undefined}
                    title="Profile"
                >
                    <CircleUserRound size={18} strokeWidth={location.pathname === '/profile' ? 2.5 : 1.5} />
                </button>

                {/* Settings dropdown */}
                <div className="relative" ref={menuRef}>
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        className="flex items-center p-2 rounded-full text-sm text-white/90 hover:bg-white/15 transition-all"
                        title="Settings"
                    >
                        <Settings size={18} strokeWidth={1.5} />
                    </button>
                    {menuOpen && (
                        <div className={`absolute right-0 mt-2 w-52 ${themeCSS.dropdownBg} rounded-xl shadow-lg ring-1 ${themeCSS.dropdownBorder} py-1 z-50`}>
                            {/* Theme options */}
                            <p className={`px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Theme</p>
                            <button
                                onClick={() => { updateTheme('light'); setMenuOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${themeCSS.dropdownText} ${themeCSS.dropdownHover} transition-colors`}
                            >
                                <Sun size={16} className="text-amber-500" />
                                Light
                                {theme === 'light' && <span className="ml-auto text-xs font-bold" style={{ color: primaryColor }}>Active</span>}
                            </button>
                            <button
                                onClick={() => { updateTheme('highContrast'); setMenuOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm ${themeCSS.dropdownText} ${themeCSS.dropdownHover} transition-colors`}
                            >
                                <EyeOff size={16} className={theme === 'highContrast' ? 'text-black' : 'text-purple-500'} />
                                High Contrast
                                {theme === 'highContrast' && <span className="ml-auto text-xs font-bold" style={{ color: primaryColor }}>Active</span>}
                            </button>

                            {/* Font size options */}
                            <div className={`${themeCSS.cardBorder} border-t my-1`} />
                            <p className={`px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider ${themeCSS.cardMuted}`}>Text Size</p>
                            <div className="flex items-center gap-1 px-4 py-2">
                                {[
                                    { key: 'normal', label: 'A', size: 'text-sm' },
                                    { key: 'large', label: 'A', size: 'text-base' },
                                    { key: 'xl', label: 'A', size: 'text-lg' },
                                ].map(opt => (
                                    <button
                                        key={opt.key}
                                        onClick={() => updateFontSize(opt.key)}
                                        className={`flex-1 flex items-center justify-center py-1.5 rounded-lg font-semibold transition-colors ${opt.size} ${
                                            fontSize === opt.key
                                                ? 'text-white'
                                                : `${themeCSS.dropdownText} ${themeCSS.dropdownHover}`
                                        }`}
                                        style={fontSize === opt.key ? { backgroundColor: primaryColor } : undefined}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>

                            <div className={`${themeCSS.cardBorder} border-t my-1`} />
                            <button
                                onClick={() => { logout(); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
